import React, { useState } from 'react';
import { 
  signInWithEmailAndPassword, 
  signInWithPopup, 
  GoogleAuthProvider 
} from 'firebase/auth';
import { collection, query, where, getDocs, doc, getDoc, setDoc } from 'firebase/firestore';
import { auth, db } from '../../config/firebase'; // Ensure db is exported from your config
import { useNavigate, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import '../../styles/Login.css';
import LOGOB from '../../assets/LOGB.webp';
import LOGB1 from '../../assets/LOGB1.webp';
import LOG from '../../assets/LOG.webp';
import ALLBOX from '../../assets/ALLBOX.webp';

const teacherEmails = ['91xfdc@gmail.com', 'ed@gmail.com']; 

const Login = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [pendingGoogleUser, setPendingGoogleUser] = useState(null);
  const [newUsername, setNewUsername] = useState('');
  
  const navigate = useNavigate();
  const googleProvider = new GoogleAuthProvider();

  const routeUser = (userEmail) => {
    if (teacherEmails.includes(userEmail)) {
      navigate('/'); // Replace with actual teacher route
    } else {
      navigate('/'); // Replace with actual student route
    }
  };

  const handleLogin = async () => {
    if (!email || !password) {
      setError('Please provide email and password.');
      return;
    }

    try {
      const userCredential = await signInWithEmailAndPassword(auth, email, password);
      routeUser(userCredential.user.email);
    } catch (error) {
      setError('Invalid email or password.');
    }
  };

  const handleGoogleLogin = async () => {
    setError('');
    try {
      const userCredential = await signInWithPopup(auth, googleProvider);
      const user = userCredential.user;

      // Check if this Google user already exists in your Firestore 'users' collection
      const usersRef = collection(db, 'users');
      const q = query(usersRef, where('email', '==', user.email));
      const querySnapshot = await getDocs(q);

      if (!querySnapshot.empty) {
        // User already has a profile, route them immediately
        routeUser(user.email);
      } else {
        // New user: Hold their auth state and ask for a username
        setPendingGoogleUser(user);
      }
    } catch (error) {
      console.error("Google Auth Error:", error);
      setError('Google sign-in failed. Please try again.');
    }
  };

  const handleSaveUsername = async () => {
    if (!newUsername.trim()) {
      setError('Username cannot be empty.');
      return;
    }

    setError('');
    const formattedUsername = newUsername.trim().toLowerCase();

    try {
      // 1. Check if the chosen username is already taken
      const userDocRef = doc(db, 'users', formattedUsername);
      const userDoc = await getDoc(userDocRef);

      if (userDoc.exists()) {
        setError('Username is already taken. Please choose another.');
        return;
      }

      // 2. Save the new user to Firestore
      await setDoc(userDocRef, {
        email: pendingGoogleUser.email,
        createdAt: new Date().toISOString()
      });

      // 3. Setup complete, route them in
      routeUser(pendingGoogleUser.email);
    } catch (error) {
      console.error("Error saving username:", error);
      setError('Failed to save username. Try again.');
    }
  };

  return (
    <div className="login-bg" style={{ backgroundImage: `url(${LOGOB})` }}>
      <motion.div
        className="login-container"
        style={{ backgroundImage: `url(${LOGB1})` }}
        initial={{ opacity: 0, y: 50 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        <motion.img
          src={LOG}
          alt="Login"
          className="login-title"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.5, delay: 0.2 }}
        />
        
        {/* --- CONDITIONAL RENDERING BASED ON GOOGLE AUTH STATE --- */}
        {!pendingGoogleUser ? (
          <>
            <motion.input
              type="email"
              placeholder="Email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="login-input"
              whileFocus={{ scale: 1.05 }}
              style={{ backgroundImage: `url(${ALLBOX})` }}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.5, delay: 0.4 }}
            />
            
            <motion.input
              type="password"
              placeholder="Password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="login-input"
              whileFocus={{ scale: 1.05 }}
              style={{ backgroundImage: `url(${ALLBOX})` }}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.5, delay: 0.6 }}
            />
            
            <motion.button
              className="login-button-image"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.5, delay: 0.8 }}
              onClick={handleLogin}
            >
              Login
            </motion.button>

            <motion.button
              className="login-button-google"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.5, delay: 0.9 }}
              onClick={handleGoogleLogin}
              style={{
                marginTop: '10px',
                backgroundColor: '#ffffff',
                color: '#757575',
                border: 'none',
                padding: '10px 20px',
                borderRadius: '4px',
                fontWeight: 'bold',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '10px',
                width: '80%' 
              }}
            >
              <svg width="18" height="18" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 48 48">
                <path fill="#FFC107" d="M43.611,20.083H42V20H24v8h11.303c-1.649,4.657-6.08,8-11.303,8c-6.627,0-12-5.373-12-12c0-6.627,5.373-12,12-12c3.059,0,5.842,1.154,7.961,3.039l5.657-5.657C34.046,6.053,29.268,4,24,4C12.955,4,4,12.955,4,24c0,11.045,8.955,20,20,20c11.045,0,20-8.955,20-20C44,22.659,43.862,21.35,43.611,20.083z"/>
                <path fill="#FF3D00" d="M6.306,14.691l6.571,4.819C14.655,15.108,18.961,12,24,12c3.059,0,5.842,1.154,7.961,3.039l5.657-5.657C34.046,6.053,29.268,4,24,4C16.318,4,9.656,8.337,6.306,14.691z"/>
                <path fill="#4CAF50" d="M24,44c5.166,0,9.86-1.977,13.409-5.192l-6.19-5.238C29.211,35.091,26.715,36,24,36c-5.202,0-9.619-3.317-11.283-7.946l-6.522,5.025C9.505,39.556,16.227,44,24,44z"/>
                <path fill="#1976D2" d="M43.611,20.083H42V20H24v8h11.303c-0.792,2.237-2.231,4.166-4.087,5.571c0.001-0.001,0.002-0.001,0.003-0.002l6.19,5.238C36.971,39.205,44,34,44,24C44,22.659,43.862,21.35,43.611,20.083z"/>
              </svg>
              Sign in with Google
            </motion.button>
          </>
        ) : (
          <>
            {/* --- NEW USERNAME SETUP UI --- */}
            <motion.p
              style={{ color: '#E0E0E0', textAlign: 'center', marginBottom: '15px', width: '80%' }}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
            >
              Welcome! Please choose a unique username to complete your profile.
            </motion.p>
            
            <motion.input
              type="text"
              placeholder="Enter username"
              value={newUsername}
              onChange={(e) => setNewUsername(e.target.value)}
              className="login-input"
              whileFocus={{ scale: 1.05 }}
              style={{ backgroundImage: `url(${ALLBOX})` }}
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
            />
            
            <motion.button
              className="login-button-image"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              onClick={handleSaveUsername}
            >
              Complete Setup
            </motion.button>
          </>
        )}

        {error && (
          <motion.p
            className="login-error"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.5, delay: 0.2 }}
          >
            {error}
          </motion.p>
        )}
        
        {!pendingGoogleUser && (
          <div className="login-signup-link">
            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.5, delay: 1.2 }}
            >
              Don't have an account? <Link to="/signup">Sign up</Link>
            </motion.p>
          </div>
        )}
      </motion.div>
    </div>
  );
};

export default Login;